import * as React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Link from '@mui/material/Link'

import { useTheme } from '@mui/material/styles'

import SendRoundedIcon from '@mui/icons-material/SendRounded'
import MicNoneIcon from '@mui/icons-material/MicNone'
import MicIcon from '@mui/icons-material/Mic'
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import LanguageIcon from '@mui/icons-material/Language'
import YouTubeIcon from '@mui/icons-material/YouTube'
import XIcon from '@mui/icons-material/X'
import GoogleIcon from '@mui/icons-material/Google'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { DialogsContext } from '../DialogsContext'
import { UserContext } from '../UserContext'
import { sendToAnalytics } from '../Analytics'
import { isKeywordGraphApp, appProductName } from '../appVariant'
import {
    useOnboardingStore,
    selectGuideSimplifiesImports,
    selectGuideBringSourceKeys,
} from '../stores/useOnboardingStore'
import { isValidUrl, getUrlTypeAndCode } from '../utils/index'
import { PanelRow, cardSx, columnSx } from './panelLayout'
import {
    ConnectCard,
    SetupPanel,
    SetupSteps,
    SetupStep,
    ClientSwitcher,
    CodeSnippet,
    StepNote,
    StepText,
} from './connectCards'
import McpServerConnect from './McpServerCard'
import { chipHoverAccent } from '../theme'
import VoiceGraphDialog, { voiceGraphUrl } from './VoiceGraphDialog'
import useVoiceDictation, {
    isVoiceDictationSupported,
} from '../hooks/useVoiceDictation'
import { transcribeVoiceAudio } from '../hooks/useApi'

// Inputs shorter than this read as a "topic / a few keywords" and open the
// source picker (shortTextImport); sentence-length inputs and longer read as a
// text/prompt and open the plain-text editor (textEditor), which also offers
// the "Use as AI Prompt to Generate a Graph" shortcut. Tuned low enough that a
// one-line prompt (e.g. the Ray Dalio sample, ~106 chars) counts as text.
const LONG_TEXT_THRESHOLD = 80
// Mid-length inputs (>= LONG_TEXT_THRESHOLD but <= this) read as an AI prompt
// and open the AI-graph generator (aiGraphs) rather than the plain-text editor.
// Anything longer than this is treated as a real text to analyse (textEditor).
const AI_PROMPT_MAX_THRESHOLD = 500

// Pasted links skip the length-based routing and open the import dialog for
// their source directly (same mapping as MainImport). Unrecognised sites fall
// back to the generic websites importer ('urls').
const URL_DIALOG_BY_TYPE = {
    amazon: 'amazonReviews',
    youtube: 'youtubeSubtitles',
    youtubeChannel: 'youtubeChannel',
    youtubePlaylist: 'youtubeChannel',
    wikipedia: 'urls',
    twitter: 'twitter',
    rss: 'rss',
}

// Only http(s) counts as a link here — bare isValidUrl would also match any
// "scheme:rest" token (e.g. "note:something"), misrouting ordinary topics.
const isHttpUrl = token => /^https?:\/\/\S+$/i.test(token) && isValidUrl(token)

// Dotted tokens whose "TLD" is really a file extension ("node.js",
// "index.html") are far more likely topic names than links.
const FILE_EXT_TLDS = new Set([
    'js',
    'jsx',
    'ts',
    'tsx',
    'css',
    'json',
    'yml',
    'yaml',
    'md',
    'txt',
    'csv',
    'xml',
    'html',
    'htm',
    'php',
    'py',
    'rb',
    'java',
    'pdf',
])

// A schemeless "domain.tld[/path]" (optionally www.) also reads as a link,
// but only when it's the whole input — checked at the call site.
const isBareDomain = token => {
    const match = /^(?:www\.)?(?:[a-z0-9-]+\.)+([a-z]{2,24})(?:[/?#]\S*)?$/i.exec(
        token
    )
    return (
        Boolean(match) &&
        !FILE_EXT_TLDS.has(match[1].toLowerCase()) &&
        isValidUrl(`https://${token}`)
    )
}

// The "most popular" source shortcuts, in the order requested. Each maps to a
// dialog `type` understood by <ImportDialog> (see components/Imports/index.jsx).
// `brandColor` overrides the glyph colour so brand marks stay recognisable.
// `hiddenDuringGuide` drops a source while the onboarding guide asks for the
// first import, so the guided user picks from the few that need no setup.
const SOURCES_KEYWORDGRAPH = [
    {
        key: 'searchResults',
        label: 'Search Results',
        type: 'google',
        Icon: GoogleIcon,
        brandColor: '#4285F4',
    },
    {
        key: 'searchIntent',
        label: 'Search Intent',
        type: 'keywords',
        Icon: ManageSearchOutlinedIcon,
        brandColor: '#34A853',
    },
    {
        key: 'llmOutput',
        label: 'LLM Output',
        type: 'aiTopics',
        Icon: AutoAwesomeOutlinedIcon,
        brandColor: '#A142F4',
    },
    {
        key: 'websites',
        hiddenDuringGuide: true,
        label: 'Websites',
        type: 'urls',
        Icon: LanguageIcon,
        brandColor: '#0097A7',
    },
    {
        key: 'csv',
        hiddenDuringGuide: true,
        label: 'CSV & Spreadsheets',
        type: 'csvFileImport',
        Icon: TableChartOutlinedIcon,
        brandColor: '#21A366',
    },
    {
        key: 'youtube',
        hiddenDuringGuide: true,
        label: 'YouTube',
        type: 'youtubeKeywords',
        Icon: YouTubeIcon,
        brandColor: '#FF0000',
    },
    {
        key: 'textFiles',
        label: 'Text Files',
        type: 'textFilesImport',
        Icon: DescriptionOutlinedIcon,
        brandColor: '#5C6BC0',
    },
    {
        key: 'copiedText',
        label: 'Plain Text',
        type: 'textEditor',
        Icon: ContentPasteOutlinedIcon,
        brandColor: '#FB8C00',
    },
]

const SOURCES_INFRANODUS = [
    {
        key: 'voice',
        label: 'Voice',
        type: 'voice',
        Icon: MicNoneIcon,
        brandColor: '#6688ff',
    },
    {
        key: 'copiedText',
        label: 'Plain Text',
        type: 'textEditor',
        Icon: ContentPasteOutlinedIcon,
        brandColor: '#FB8C00',
    },
    {
        key: 'aiPrompt',
        label: 'AI Prompt',
        type: 'aiGraphs',
        Icon: AutoAwesomeOutlinedIcon,
        brandColor: '#A142F4',
    },
    {
        key: 'documents',
        label: 'PDF, TXT & Markdown',
        type: 'textFilesImport',
        Icon: DescriptionOutlinedIcon,
        brandColor: '#5C6BC0',
    },
    {
        key: 'csv',
        hiddenDuringGuide: true,
        label: 'CSV & Spreadsheets',
        type: 'csvFileImport',
        Icon: TableChartOutlinedIcon,
        brandColor: '#21A366',
    },
    {
        key: 'websites',
        hiddenDuringGuide: true,
        label: 'Websites',
        type: 'urls',
        Icon: LanguageIcon,
        brandColor: '#0097A7',
    },
    {
        key: 'youtube',
        hiddenDuringGuide: true,
        label: 'YouTube',
        type: 'youtubeSubtitles',
        Icon: YouTubeIcon,
        brandColor: '#FF0000',
    },
    {
        key: 'x',
        hiddenDuringGuide: true,
        label: 'X posts',
        type: 'twitter',
        Icon: XIcon,
        brandColor: 'text.primary',
    },
    {
        key: 'google',
        label: 'Google',
        type: 'google',
        Icon: GoogleIcon,
        brandColor: '#4285F4',
    },
    // Related search queries (demand), the other half of a Google results
    // graph. Offered to the SEO persona's first step only; the import itself
    // is in the dialog for everyone ("Search Intent Keywords").
    {
        key: 'searchIntent',
        guideOnly: true,
        label: 'Search Intent',
        type: 'keywords',
        Icon: ManageSearchOutlinedIcon,
        brandColor: '#34A853',
    },
]

// Quick suggestions to seed exploration. Short topics open the source picker
// on click; `populateOnly` ones (e.g. a longer AI prompt) just fill the box to
// show that longer prompts are possible.
const SUGGESTIONS_INFRANODUS = [
    { label: 'ai agents', value: 'ai agents' },
    { label: 'spacex', value: 'spacex' },
    { label: 'nanomaterials', value: 'nanomaterials' },
    {
        label: 'Ray Dalio principles',
        value:
            'generate a graph of the main principles of Ray Dalio as applied to finance, investment, life, and business',
        populateOnly: true,
    },
]

const SUGGESTIONS_KEYWORDGRAPH = [
    { label: 'ai agents', value: 'ai agents' },
    { label: 'spacex', value: 'spacex' },
    { label: 'nanomaterials', value: 'nanomaterials' },
    {
        label: 'How AI sees the "longevity" topic',
        value:
            'Generate a graph that maps out the main ideas around the "longevity" topic, including health, fitness, and lifestyle implications.',
        populateOnly: true,
    },
]

export default function QuickStartSection() {
    const theme = useTheme()
    const isKeywordGraph = isKeywordGraphApp()
    // Subscribed, not sampled at mount as the import dialogs do: eligibility
    // resolves asynchronously, so a first-session user's /apps often mounts
    // while the guide is still 'unknown' and only turns 'active' afterwards.
    const guideSimplifiesImports = useOnboardingStore(
        selectGuideSimplifiesImports
    )
    const guideSourceKeys = useOnboardingStore(selectGuideBringSourceKeys)
    const allSources = isKeywordGraph
        ? SOURCES_KEYWORDGRAPH
        : SOURCES_INFRANODUS
    // The persona's own list when it has one; otherwise the sources that need
    // no setup. `guideOnly` sources exist for a persona's first step alone.
    const sourceOptions = guideSourceKeys
        ? allSources.filter(source => guideSourceKeys.includes(source.key))
        : allSources.filter(
              source =>
                  !source.guideOnly &&
                  !(guideSimplifiesImports && source.hiddenDuringGuide)
          )
    const suggestions = isKeywordGraph
        ? SUGGESTIONS_KEYWORDGRAPH
        : SUGGESTIONS_INFRANODUS

    // The skill card swaps between its collapsed form in the rail and its
    // full-width setup panel (the MCP one carries its own toggle).
    const [showObsidian, setShowObsidian] = React.useState(false)

    // The descriptive blurb under the heading is hidden by default and revealed
    // by the little info button to the right of the heading.
    const [showInfo, setShowInfo] = React.useState(false)

    const { username } = React.useContext(UserContext)

    // "Voice" source chip: name a graph, then land in it with the mic on.
    const [voiceDialogOpen, setVoiceDialogOpen] = React.useState(false)
    const startVoiceGraph = ({ name, source, includeMic }) => {
        sendToAnalytics({
            userId: username,
            event: 'create_new_graph',
            data: {
                source: 'apps_quickstart_source',
                type: 'voice',
                voiceSource: source,
            },
        })
        window.location.href = voiceGraphUrl(username, name, {
            source,
            includeMic,
        })
    }

    // Mic inside the search box: dictate the topic/text into the field. One
    // utterance at a time — listening stops once its transcript has landed.
    const [livePreview, setLivePreview] = React.useState('')
    const [voiceError, setVoiceError] = React.useState(null)
    const fieldVoice = useVoiceDictation({
        transcribe: (blob, filename, meta) =>
            transcribeVoiceAudio(blob, filename, null, meta),
        onPreview: setLivePreview,
        onStatement: ({ text, statements }) => {
            const spoken = [
                text,
                ...statements.map(statement => statement.text),
            ]
                .filter(Boolean)
                .join(' ')
            if (spoken) {
                setPresetSearchString(current =>
                    [current?.trim(), spoken].filter(Boolean).join(' ')
                )
            }
            fieldVoice.stop()
        },
        onError: err => setVoiceError(err?.message || 'Voice input failed.'),
    })
    const canDictateIntoField = !isKeywordGraph && isVoiceDictationSupported()
    const toggleFieldVoice = () => {
        if (fieldVoice.isListening) {
            fieldVoice.stop()
            return
        }
        setVoiceError(null)
        fieldVoice.start({ source: 'mic' })
    }
    const {
        setActiveImportDialog,
        presetSearchString,
        setPresetSearchString,
    } = React.useContext(DialogsContext)

    // Bind the hero box to the SAME shared value the import dialogs read, so
    // whatever the user types here is already present when the dialog opens —
    // exactly as if they had typed it into ShortTextImport's text field.
    const value = presetSearchString || ''
    // While dictating, the field shows the words as they are recognised.
    const fieldValue = fieldVoice.isListening
        ? [value.trim(), livePreview].filter(Boolean).join(' ')
        : value
    const trimmedValue = value.trim()
    const trimmedLength = trimmedValue.length
    // One pasted link — or several, whitespace/newline-separated — reads as a
    // URL import, regardless of length. A bare domain ("noduslabs.com",
    // "www.wired.com") counts too, but only as the entire input, so multi-word
    // topics that happen to contain a dotted token keep reading as topics.
    const urlTokens = trimmedValue.split(/\s+/)
    const isUrlInput = trimmedLength > 0 && urlTokens.every(isHttpUrl)
    const isBareDomainInput =
        !isUrlInput && urlTokens.length === 1 && isBareDomain(trimmedValue)
    const isLinkInput = isUrlInput || isBareDomainInput
    const isLongText = trimmedLength >= LONG_TEXT_THRESHOLD
    // Long-but-not-huge input (<= 500 chars) is treated as an AI prompt and
    // routed to the AI-graph generator; only genuinely large pastes open the
    // plain-text editor.
    const isAiPromptText =
        isLongText && trimmedLength <= AI_PROMPT_MAX_THRESHOLD

    const openImport = (type, source) => {
        // Snapshot whatever the user typed here as the "first_search" so the
        // server-side import log can compare it with the query they ultimately
        // submit (which they may edit inside the import dialog).
        try {
            if (value && value.trim()) {
                window.sessionStorage.setItem('firstSearch', value.trim())
            }
        } catch (err) {
            // sessionStorage may be unavailable (private mode); ignore.
        }
        setActiveImportDialog(type)
        sendToAnalytics({
            userId: username,
            event: 'create_new_graph',
            data: { source: source || 'apps_quickstart', type },
        })
    }

    // Submitting the box mirrors entering a value into ShortTextImport: a short
    // input is treated as a topic and opens the source picker; a long paste is
    // treated as plain text and opens the text-analysis panel directly.
    const handleSubmit = () => {
        if (isLinkInput) {
            // The import dialogs expect a full http(s) URL, so a bare domain
            // gets the scheme prepended (also into the shared preset string
            // the dialog reads).
            const normalized = isBareDomainInput
                ? `https://${trimmedValue}`
                : trimmedValue
            if (isBareDomainInput) {
                setPresetSearchString(normalized)
            }
            // A single link routes to its source's import dialog (YouTube,
            // Amazon, X, …); several links go to the websites importer.
            const type =
                urlTokens.length === 1
                    ? URL_DIALOG_BY_TYPE[getUrlTypeAndCode(normalized).type] ||
                      'urls'
                    : 'urls'
            openImport(type, 'apps_quickstart_search')
            return
        }
        let type = 'shortTextImport'
        if (isAiPromptText) {
            type = 'aiGraphs'
        } else if (isLongText) {
            type = 'textEditor'
        }
        openImport(type, 'apps_quickstart_search')
    }

    const handleKeyDown = event => {
        // Enter submits, Shift+Enter inserts a newline (so longer pastes can be
        // edited in place before sending).
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            handleSubmit()
        }
    }

    return (
        <PanelRow sx={{ mt: 3, mb: 5, mr: { xs: 2, sm: 4 } }}>
            <Box
                data-testid="quickstart-build-card"
                sx={{ ...cardSx(theme), ...columnSx }}
            >
                {/* ---- Hero heading (matches "Explore … Applications" below) ---- */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: showInfo ? 0 : 3,
                    }}
                >
                    <Typography
                        component="h5"
                        variant="h5"
                        align="left"
                        color="text.primary"
                    >
                        {isKeywordGraph
                            ? 'Explore a search query or a topic'
                            : 'Build a knowledge graph from any text or topic'}
                    </Typography>
                    <Tooltip
                        title={showInfo ? 'Hide details' : 'What is this?'}
                    >
                        <IconButton
                            size="small"
                            aria-label="Toggle description"
                            aria-expanded={showInfo}
                            onClick={() => setShowInfo(prev => !prev)}
                            sx={{
                                color: showInfo
                                    ? 'text.secondary'
                                    : 'text.disabled',
                            }}
                        >
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                {showInfo && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, mb: 3, maxWidth: 620 }}
                    >
                        {isKeywordGraph ? (
                            <>
                                Build a graph of the search results, search
                                queries, or LLM output to reveal the main
                                topical clusters and content gaps:
                            </>
                        ) : (
                            <>
                                Paste your notes, a document, or just type a
                                topic — {appProductName()} reveals the main
                                ideas, the gaps, and the connections you'd
                                otherwise miss.
                            </>
                        )}
                    </Typography>
                )}

                {/* ---- Hero search / paste box ---- */}
                <Box sx={{ maxWidth: 720 }}>
                    {/* Relative wrapper + absolutely-positioned button keeps the
                        send control reliably INSIDE the rounded border regardless
                        of MUI's multiline-adornment padding quirks. */}
                    <Box sx={{ position: 'relative' }}>
                        <TextField
                            value={fieldValue}
                            onChange={e =>
                                setPresetSearchString(e.target.value)
                            }
                            onKeyDown={handleKeyDown}
                            placeholder={
                                fieldVoice.isListening
                                    ? 'listening… say the topic or text, then pause'
                                    : 'Type a topic to explore, or paste your text here…'
                            }
                            InputProps={{ readOnly: fieldVoice.isListening }}
                            variant="outlined"
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={8}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '14px',
                                    // Taller box; symmetric padding also keeps a
                                    // single line vertically centred.
                                    py: 1.75,
                                    // Reserve room on the right so neither text
                                    // nor placeholder slides under the buttons.
                                    pr: isKeywordGraph ? '52px' : '92px',
                                    '& fieldset': {
                                        borderWidth: 2,
                                        borderColor: 'info.main',
                                    },
                                    '&:hover fieldset': {
                                        borderWidth: 2,
                                        borderColor: 'info.dark',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderWidth: 2,
                                    },
                                },
                            }}
                        />
                        {/* Vertically centred against the input: top/bottom 0 +
                            flex centring keeps the send button mid-height as the
                            box grows, and clear of the notched-outline border. */}
                        <Box
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            {canDictateIntoField && (
                                <Tooltip
                                    title={
                                        fieldVoice.isListening
                                            ? 'Stop dictation'
                                            : 'Dictate into this field'
                                    }
                                    placement="top"
                                >
                                    <IconButton
                                        onClick={toggleFieldVoice}
                                        aria-label={
                                            fieldVoice.isListening
                                                ? 'Stop dictation'
                                                : 'Dictate into the search field'
                                        }
                                        aria-pressed={fieldVoice.isListening}
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            color: fieldVoice.isListening
                                                ? 'error.main'
                                                : 'info.main',
                                            '@keyframes quickVoicePulse': {
                                                '0%, 100%': { opacity: 1 },
                                                '50%': { opacity: 0.35 },
                                            },
                                            animation: fieldVoice.isSpeaking
                                                ? 'quickVoicePulse 0.8s ease-in-out infinite'
                                                : 'none',
                                        }}
                                    >
                                        {fieldVoice.isListening ? (
                                            <MicIcon sx={{ fontSize: 18 }} />
                                        ) : (
                                            <MicNoneIcon
                                                sx={{ fontSize: 18 }}
                                            />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip
                                title={
                                    isLinkInput
                                        ? 'Import content from this link'
                                        : isLongText
                                        ? 'Analyze this text'
                                        : 'Explore this topic'
                                }
                                placement="top"
                            >
                                <IconButton
                                    color="info"
                                    onClick={handleSubmit}
                                    aria-label="Continue"
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        bgcolor: 'info.main',
                                        color: 'info.contrastText',
                                        '&:hover': { bgcolor: 'info.dark' },
                                    }}
                                >
                                    <SendRoundedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        {!isKeywordGraph && (
                            <VoiceGraphDialog
                                open={voiceDialogOpen}
                                onClose={() => setVoiceDialogOpen(false)}
                                onStart={startVoiceGraph}
                            />
                        )}
                    </Box>

                    {voiceError && (
                        <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: 'block', mt: 1 }}
                        >
                            {voiceError}
                        </Typography>
                    )}

                    {/* Adaptive hint + topic suggestions */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 1,
                            mt: 1.5,
                            minHeight: 32,
                        }}
                    >
                        {value.trim().length === 0 ? (
                            <>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontWeight: 500 }}
                                >
                                    try one of these:
                                </Typography>
                                {suggestions.map(suggestion => (
                                    <Chip
                                        key={suggestion.label}
                                        label={suggestion.label}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                            setPresetSearchString(
                                                suggestion.value
                                            )
                                            // Longer prompt chips just populate
                                            // the box so the user sees a fuller
                                            // prompt before submitting.
                                            if (!suggestion.populateOnly) {
                                                openImport(
                                                    'shortTextImport',
                                                    'apps_quickstart_suggestion'
                                                )
                                            } else {
                                                // populateOnly chips don't launch
                                                // an import, so track the click
                                                // explicitly (otherwise it would
                                                // go unrecorded).
                                                sendToAnalytics({
                                                    userId: username,
                                                    event:
                                                        'quickstart_chip_populate',
                                                    data: {
                                                        source:
                                                            'apps_quickstart_suggestion',
                                                        type: 'shortTextImport',
                                                        label: suggestion.label,
                                                    },
                                                })
                                            }
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            fontWeight: 500,
                                            borderColor:
                                                'rgba(0, 200, 200, 0.5)',
                                            color: 'info.main',
                                            // Deepen the blue instead of
                                            // inverting to a filled chip. No
                                            // bgcolor, so the Chip keeps MUI's
                                            // own subtle clickable tint.
                                            '&:hover': {
                                                color: chipHoverAccent,
                                                borderColor: chipHoverAccent,
                                            },
                                        }}
                                    />
                                ))}
                            </>
                        ) : (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {isLinkInput
                                    ? 'Looks like a link — press Enter to import and analyze its content.'
                                    : isLongText
                                    ? 'Looks like a text — press Enter to analyze it as plain text.'
                                    : 'Press Enter to explore this topic and choose a source.'}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* ---- Most popular sources ---- */}
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{
                        display: 'block',
                        mt: 4,
                        mb: 1.5,
                        letterSpacing: 1,
                    }}
                >
                    or generate from
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        flexWrap: { sm: 'wrap' },
                        gap: 1.5,
                        maxWidth: 720,
                    }}
                >
                    {sourceOptions.map(
                        ({ key, label, type, Icon, brandColor }) => (
                            <Button
                                key={key}
                                data-testid={`quickstart-source-${key}`}
                                variant="outlined"
                                color="inherit"
                                startIcon={
                                    <Icon
                                        sx={{
                                            color:
                                                brandColor || 'text.secondary',
                                        }}
                                    />
                                }
                                onClick={() =>
                                    type === 'voice'
                                        ? setVoiceDialogOpen(true)
                                        : openImport(
                                              type,
                                              'apps_quickstart_source'
                                          )
                                }
                                sx={{
                                    justifyContent: {
                                        xs: 'flex-start',
                                        sm: 'center',
                                    },
                                    width: { xs: '100%', sm: 'auto' },
                                    borderRadius: '999px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 2.5,
                                    py: 1,
                                    color: 'text.primary',
                                    borderColor: 'divider',
                                    bgcolor:
                                        theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.03)'
                                            : 'rgba(0,0,0,0.015)',
                                    '&:hover': {
                                        borderColor: 'info.main',
                                        bgcolor:
                                            theme.palette.mode === 'dark'
                                                ? 'rgba(255,255,255,0.06)'
                                                : 'rgba(0,0,0,0.03)',
                                    },
                                }}
                            >
                                {label}
                            </Button>
                        )
                    )}
                </Box>

                {/* ---- Escape hatch ---- */}
                <Link
                    href="/import"
                    underline="hover"
                    color="info.main"
                    sx={{
                        // Block-level (not inline-flex) so `mt` actually
                        // separates it from the source buttons above.
                        mt: 2.5,
                        display: 'flex',
                        width: 'fit-content',
                        alignItems: 'center',
                        gap: 0.5,
                        fontWeight: 600,
                    }}
                >
                    Browse all import sources
                    <ArrowForwardOutlinedIcon sx={{ fontSize: 16 }} />
                </Link>
            </Box>

            {/* ---- Connect-your-tools column. Beside the build card on wide
                 screens, stacked under it otherwise. Each entry swaps between
                 its collapsed card and its setup panel IN PLACE, so opening one
                 never reorders the two. ---- */}
            <Box
                data-testid="quickstart-connect-column"
                sx={{
                    ...columnSx,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <McpServerConnect />
                {showObsidian ? (
                    <AgentSkillCard onHide={() => setShowObsidian(false)} />
                ) : (
                    <ConnectCard
                        title={
                            isKeywordGraph
                                ? 'Run the SEO Skill in Your AI Agent'
                                : 'Graph your local files and knowledge bases'
                        }
                        description={
                            isKeywordGraph
                                ? 'Let your agent map search intent, read the SERP and return a prioritized SEO plan.'
                                : 'Point an AI agent at your vault and let it graph your notes to reveal themes and gaps.'
                        }
                        snippet={
                            isKeywordGraph
                                ? 'npx skills add noduslabs/claude-seo-skill'
                                : 'npx skills add infranodus/skills --skill infranodus'
                        }
                        onOpen={() => setShowObsidian(true)}
                    />
                )}
            </Box>
        </PanelRow>
    )
}

// Agent-skill setup card, per variant. InfraNodus: the Knowledge Graph skill
// (the `infranodus` skill from infranodus/skills) so an AI agent can graph an
// Obsidian vault. KeywordGraph: the SEO Analysis skill from
// noduslabs/claude-seo-skill (mirrors keywordgraph.com/seo-skill/). A compact
// version of the /skills page stepper — keep the commands in step with it.
function AgentSkillCard({ onHide }) {
    const isKG = isKeywordGraphApp()
    const repo = isKG ? 'noduslabs/claude-seo-skill' : 'infranodus/skills'
    const npxBase = isKG
        ? `npx skills add ${repo}`
        : `npx skills add ${repo} --skill infranodus`
    const agents = {
        'claude-code': {
            label: 'Claude Code',
            body: (
                <>
                    <CodeSnippet
                        text={
                            isKG
                                ? `/plugin marketplace add ${repo}\n/plugin install seo-analysis@keywordgraph-skills`
                                : `/plugin marketplace add ${repo}\n/plugin install infranodus@infranodus-skills`
                        }
                    />
                    <StepText sx={{ mt: 0.5 }}>
                        Run both in the Claude Code prompt. Or in your terminal:
                    </StepText>
                    <CodeSnippet text={`${npxBase} -g -a claude-code -y`} />
                </>
            ),
        },
        'claude-desktop': {
            label: 'Claude Desktop',
            body: (
                <StepNote>
                    Download the skill{' '}
                    <Link
                        href={
                            isKG
                                ? `https://github.com/${repo}`
                                : `https://github.com/${repo}/releases/latest`
                        }
                        underline="hover"
                        target="_blank"
                    >
                        {isKG ? 'from GitHub' : 'as a .zip'}
                    </Link>
                    , then in Claude Web / Desktop open{' '}
                    <strong>Customize &gt; Skills</strong>, enable code
                    execution and click <strong>Upload Skill</strong>.
                </StepNote>
            ),
        },
        ...(isKG
            ? {}
            : {
                  codex: {
                      label: 'Codex',
                      body: (
                          <>
                              <CodeSnippet
                                  text={`$skill-installer install the infranodus skill from https://github.com/${repo}`}
                                  wrap
                              />
                              <StepText sx={{ mt: 0.5 }}>
                                  Type this in the Codex prompt. Or in your
                                  terminal:
                              </StepText>
                              <CodeSnippet text={`${npxBase} -g -a codex -y`} />
                          </>
                      ),
                  },
              }),
        other: {
            label: 'Other agents',
            body: (
                <>
                    <CodeSnippet text={`${npxBase} -g`} />
                    <StepNote>
                        When prompted, pick the agents to add it to — e.g.
                        cursor, codex, or claude-code.
                    </StepNote>
                </>
            ),
        },
    }
    const [agent, setAgent] = React.useState('claude-code')
    const firstPrompt = isKG
        ? 'Run an SEO analysis for [your topic]: map the search intent, analyze the top Google results, find the content gaps, and give me a prioritized plan for SEO and AI search.'
        : 'Use the InfraNodus skill on this folder: build the knowledge graph, show me the main topical clusters and the most central concepts, then name the biggest gap between two clusters and one question that would bridge it.'

    return (
        <SetupPanel
            title={
                isKG
                    ? 'Run the full SEO workflow with the SEO Analysis skill'
                    : 'Graph your Obsidian vault or local files folder with the Knowledge Graph skill'
            }
            onHide={onHide}
        >
            <SetupSteps>
                <SetupStep n={1} title="Connect the MCP server">
                    <StepText>
                        Set it up with the card above and your{' '}
                        <Link
                            href="/api-access"
                            underline="hover"
                            target="_blank"
                        >
                            API key
                        </Link>{' '}
                        — the skill calls its tools.
                    </StepText>
                </SetupStep>
                <SetupStep
                    n={2}
                    title={
                        <>
                            Install the{' '}
                            <Link
                                href={
                                    isKG
                                        ? '/seo-skill/'
                                        : '/skills/knowledge-graph'
                                }
                                underline="hover"
                                target="_blank"
                            >
                                {isKG
                                    ? 'SEO Analysis skill'
                                    : 'Knowledge Graph skill'}
                            </Link>
                        </>
                    }
                >
                    <ClientSwitcher
                        options={Object.fromEntries(
                            Object.entries(agents).map(([k, a]) => [k, a.label])
                        )}
                        value={agent}
                        onChange={setAgent}
                    />
                    {agents[agent].body}
                </SetupStep>
                <SetupStep n={3} title="Try your first prompt">
                    <StepText>
                        {isKG
                            ? 'Paste this into your agent:'
                            : 'Open your agent in the vault folder and paste:'}
                    </StepText>
                    <CodeSnippet text={firstPrompt} wrap />
                    <StepText sx={{ mt: 0.75, mb: 0 }}>
                        {isKG ? (
                            <>
                                More on the{' '}
                                <Link
                                    href="/seo-skill/"
                                    underline="hover"
                                    target="_blank"
                                >
                                    SEO Skill page
                                </Link>
                            </>
                        ) : (
                            <>
                                Browse{' '}
                                <Link
                                    href="/skills"
                                    underline="hover"
                                    target="_blank"
                                >
                                    all our skills
                                </Link>{' '}
                                · see the graphs in your vault with the{' '}
                                <Link
                                    href="/obsidian-plugin"
                                    underline="hover"
                                    target="_blank"
                                >
                                    Obsidian plugin
                                </Link>
                            </>
                        )}
                    </StepText>
                </SetupStep>
            </SetupSteps>
        </SetupPanel>
    )
}
